import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { get, post } from "helpers/api_helper"
import { store as socketStore } from "components/Common/Socket/store"
import { newPlayerOperationId } from "pages/UserAccounts/PlayerAccounts/playerInvitationState"

export const maintenanceError = code => ({
  MAINTENANCE_REVISION_CONFLICT: "Settings changed since you opened the page. Reload them and review your changes again.",
  SUPER_ADMIN_REQUIRED: "Your session no longer has SuperAdmin access.",
  INVALID_MAINTENANCE_REQUEST: "Review the reason, maintenance setting and IP addresses.",
  OPERATION_PAYLOAD_CONFLICT: "This request identifier belongs to a different change. Check its original result.",
}[code] || "The change could not be confirmed. Check the saved settings before trying again.")

export default function useMaintenanceOperation({ actorId, onSaved }) {
  const { state: { socket: { socket, socketClientId } = {} } = {} } = useContext(socketStore)
  const [state, setState] = useState({ status: "idle" })
  const active = useRef(null), saved = useRef(onSaved)
  saved.current = onSaved
  const key = "sportya-maintenance-operation:" + actorId
  const observe = useCallback((operation, submit = false) => {
    active.current?.cancel()
    const watcher = { ended: false, attempts: 0, polls: 0, readFailures: 0 }
    active.current = watcher
    const current = () => active.current === watcher && !watcher.ended
    const cleanup = () => { clearTimeout(watcher.timer); socket?.off("accounts-response", onSocket) }
    const poll = async () => {
      if (!current() || watcher.reading) return
      watcher.reading = true
      watcher.polls += 1
      try {
        const response = await get(`/settings-maintenance/operations/${operation.operationId}`, { timeout: 5000 })
        const receipt = response?.data
        if (!current()) return
        if (receipt?.operationId !== operation.operationId || receipt.actorId !== actorId || !["pending", "completed", "failed"].includes(receipt.status)) throw new Error("Invalid receipt")
        watcher.readFailures = 0
        if (receipt.status === "pending" && watcher.polls >= 20) setState({ status: "unknown", message: "The save is still awaiting confirmation. You can retry safely using the same request." })
        if (["completed", "failed"].includes(receipt.status)) {
          watcher.ended = true; cleanup()
          try { sessionStorage.removeItem(key) } catch { /* Receipt remains on the server. */ }
          setState({ status: receipt.status, receipt, message: receipt.status === "completed" ? "Changes saved." : maintenanceError(receipt.message) })
          if (receipt.status === "completed") saved.current?.(receipt)
        }
      } catch (error) {
        if (current() && [401, 403].includes(error.response?.status)) {
          watcher.ended = true; cleanup()
          setState({ status: "denied", message: maintenanceError("SUPER_ADMIN_REQUIRED") })
        } else if (current() && ++watcher.readFailures >= 3) {
          setState({ status: "unknown", message: "Save has not been confirmed. Its result is being checked; retry uses the same request." })
        }
      } finally {
        watcher.reading = false
        if (current()) { clearTimeout(watcher.timer); watcher.timer = setTimeout(poll, watcher.polls < 20 ? 1500 : 10000) }
      }
    }
    const onSocket = message => {
      if (message?.operation === "website-maintenance" && message.operationId === operation.operationId) void poll()
    }
    watcher.cancel = () => { watcher.ended = true; cleanup() }
    watcher.send = async () => {
      if (!current() || watcher.sending) return
      watcher.sending = true
      const first = ++watcher.attempts === 1 && submit
      try {
        await post("/admin/website-maintenance", { ...operation.payload, operationId: operation.operationId, socketClientId },
          { timeout: 10000, headers: { "X-Correlation-Id": first ? operation.correlationId : newPlayerOperationId() } })
      } catch (error) {
        if (!current()) return
        if (first && [400, 401, 403, 404, 422].includes(error.response?.status)) {
          watcher.ended = true; cleanup()
          try { sessionStorage.removeItem(key) } catch { /* No command was accepted. */ }
          setState({ status: "failed", message: maintenanceError([401, 403].includes(error.response.status) ? "SUPER_ADMIN_REQUIRED" : "INVALID_MAINTENANCE_REQUEST") })
        } else setState({ status: "unknown", message: "Save has not been confirmed. Its result is being checked; retry uses the same request." })
      } finally { watcher.sending = false }
    }
    setState({ status: "pending" })
    socket?.on("accounts-response", onSocket)
    watcher.timer = setTimeout(poll, submit ? 500 : 0)
    if (submit) void watcher.send()
  }, [actorId, key, socket, socketClientId])
  useEffect(() => {
    setState({ status: "idle" })
    if (actorId) try {
      const stored = JSON.parse(sessionStorage.getItem(key) || "null")
      if (stored?.actorId === actorId && /^[a-zA-Z0-9-]{16,128}$/.test(stored.operationId || "") && stored.correlationId
        && typeof stored.payload?.reason === "string" && Number.isSafeInteger(stored.payload.expectedRevision)) observe(stored)
    } catch { /* Invalid local storage must never initiate a mutation. */ }
    return () => { active.current?.cancel(); active.current = null }
  }, [actorId, key, observe])
  const start = payload => {
    if (!actorId || (active.current && !active.current.ended)) return false
    try {
      const operation = { actorId, operationId: newPlayerOperationId(), correlationId: newPlayerOperationId(), payload }
      sessionStorage.setItem(key, JSON.stringify(operation))
      observe(operation, true)
      return true
    } catch { setState({ status: "failed", message: "Session storage is unavailable. Enable it so the save can be recovered after reload." }); return false }
  }
  return { ...state, start, pending: ["pending", "unknown"].includes(state.status), retry: () => active.current?.send?.() }
}
